import { Component, Label, Node, Sprite, SpriteFrame, Toggle, _decorator } from 'cc';
import NumberFormatter from '../../../../common/scripts/Toolkit/Components/Label/NumberFormatter';
import MultiThemeSource from '../../../../common/scripts/Toolkit/Components/MultiTheme/MultiThemeSource';
import DataType from '../../../../tableEngine/Script/Game/Data/Config/DataStructure';
import CommonGamePreviewData from '../../../../tableEngine/Script/Game/Data/DataObject/PreviewData/CommonGamePreviewData';
import HallModel from '../../../../tableEngine/Script/Game/Data/Model/HallModel';
import { MachineState, PlayType } from '../../../../tableEngine/Script/Game/Data/Model/TableModel';
import Lobby from '../../../System/Lobby';
import LobbyMultiplePlayGamePreviewData from '../../Data/DataObject/PreviewData/LobbyMultiplePlayGamePreviewData';
import LobbyRoundPlayGamePreviewData from '../../Data/DataObject/PreviewData/LobbyRoundPlayGamePreviewData';
import LobbySinglePlayGamePreviewData from '../../Data/DataObject/PreviewData/LobbySinglePlayGamePreviewData';
import { LobbyGamePreviewData } from '../../Data/Model/LobbyHallModel';

const { ccclass, property, requireComponent } = _decorator;

@ccclass('MachinePortal')
@requireComponent(MultiThemeSource)
export default class MachinePortal extends Component {
    @property(Sprite)
    private readonly machineIcon: Sprite = null;

    @property(Label)
    private readonly machineNameLabel: Label = null;

    @property(NumberFormatter)
    private readonly totalSeatCountNumber: NumberFormatter = null;

    @property(NumberFormatter)
    private readonly leftSeatCountNumber: NumberFormatter = null;

    @property(NumberFormatter)
    private readonly liveRTP: NumberFormatter = null;

    @property(NumberFormatter)
    private readonly playerCount: NumberFormatter = null;

    @property(NumberFormatter)
    private readonly totalBet: NumberFormatter = null;

    @property(Toggle)
    private readonly favoriteToggle: Toggle = null;

    @property(Node)
    private readonly outOfServiceHint: Node = null;

    @property(Node)
    private readonly linkJackpotRoot: Node = null;

    @property(NumberFormatter)
    private readonly linkJackpotValue: NumberFormatter = null;

    private _themeSource: MultiThemeSource = null;
    private _displayData: LobbyGamePreviewData = null;
    private _state: MachineState = null;

    public get displayData() {
        return this._displayData;
    }

    private get themeSource() {
        if (!this._themeSource) {
            this._themeSource = this.getComponent(MultiThemeSource);
        }
        return this._themeSource;
    }

    public onLoad() {
        this.favoriteToggle.on(Toggle.EventType.TOGGLE, this.onFavoriteToggleChanged, this);
        Lobby.hall.on(HallModel.EVENT_TYPE.STATS_DATA_RECEIVED, () => {
            if (this._displayData) {
                this._updateStatsData(this._displayData);
            }
        });
    }

    public display(data: LobbyGamePreviewData) {
        if (this._displayData === data) {
            return;
        }
        const { GameType: gameType, IsFavorite: isFavorite, DisplayName: displayName, Name: name } = data;
        this.themeSource.theme = PlayType.map(gameType);
        this.machineNameLabel.string = displayName || name.toUpperCase();

        // 避免還沒 Loading 完就被切換到其他遊戲，導致顯示錯誤
        this.machineIcon.spriteFrame = null;

        this._handleDataUpdatedEvent(data);
        this._updateStatsData(data);
        this.onStateChanged(data.state);

        this.machineIcon.spriteFrame = data.getImage();

        this._displayData = data;
        this.favoriteToggle.isChecked = isFavorite;
    }

    protected onStateChanged(state: MachineState) {
        // this.multiThemeSource.theme = state;
        this.outOfServiceHint.active = state === MachineState.Maintenance;
        // this.comingSoonHint.active = state === MachineState.ComingSoon;
    }

    protected async onFavoriteToggleChanged() {
        if (this.displayData.GameType === DataType.Enums.PlayType.Single) {
            return;
        }
        this.displayData.IsFavorite = this.favoriteToggle.isChecked;
    }

    private _handleDataUpdatedEvent(data: LobbyGamePreviewData) {
        this.displayData?.off(LobbyRoundPlayGamePreviewData.EVENT_TYPE.STATS_DATA_CHANGED, this._updateStatsData, this);
        data.on(LobbyRoundPlayGamePreviewData.EVENT_TYPE.STATS_DATA_CHANGED, this._updateStatsData, this);

        this.displayData?.off(CommonGamePreviewData.EVENT_TYPE.MACHINE_STATE_CHANGED, this.onStateChanged, this);
        data.on(CommonGamePreviewData.EVENT_TYPE.MACHINE_STATE_CHANGED, this.onStateChanged, this);

        this.displayData?.off(CommonGamePreviewData.EVENT_TYPE.FAVORITE_STATUS_CHANGED, this._updateFavoriteStatus, this);
        data.on(CommonGamePreviewData.EVENT_TYPE.FAVORITE_STATUS_CHANGED, this._updateFavoriteStatus, this);

        this.displayData?.off(CommonGamePreviewData.EVENT_TYPE.IMAGE_CHANGED, this._updateLogo, this);
        data.on(CommonGamePreviewData.EVENT_TYPE.IMAGE_CHANGED, this._updateLogo, this);
    }

    private _updateStatsData(data: LobbyGamePreviewData = this.displayData) {
        const { GameType: gameType, ProgressiveValue: progressiveValue } = data;
        if (gameType === DataType.Enums.PlayType.Single) {
            const { TableCount: tableCount, remainingTableCount } = data as LobbySinglePlayGamePreviewData;
            this.totalSeatCountNumber.value = tableCount;
            this.leftSeatCountNumber.value = remainingTableCount;
        } else if (gameType === DataType.Enums.PlayType.Multiple) {
            const { RTP: rtp, PlayerCount: playerCount, TotalBet: totalBet } = data as LobbyMultiplePlayGamePreviewData;
            this.liveRTP.value = rtp * 100;
            this.playerCount.value = playerCount;
            this.totalBet.value = totalBet;
        } else if (gameType === DataType.Enums.PlayType.Round) {
            const { RTP: rtp, PlayerCount: playerCount, TotalBet: totalBet } = data as LobbyRoundPlayGamePreviewData;
            this.liveRTP.value = rtp * 100;
            this.playerCount.value = playerCount;
            this.totalBet.value = totalBet;
        }
        this.linkJackpotRoot.active = !!progressiveValue;
        this.linkJackpotValue.value = progressiveValue ?? 0;
    }

    private async _updateFavoriteStatus() {
        this.favoriteToggle.isChecked = this.displayData.IsFavorite;
        await Lobby.user.updateFavorite(this.displayData.GameID, this.displayData.GameType, this.displayData.IsFavorite);
    }

    private async _updateLogo(icon: SpriteFrame) {
        this.machineIcon.spriteFrame = icon;
    }
}
